import React, { Suspense, useState, useEffect } from 'react';
import CategoryRail from './CategoryRail';
import Header from './common/Header';
import Footer from './common/Footer';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorBoundary from './common/ErrorBoundary';
import spotlightService from '../services/SpotlightService';
import { cacheService } from '../services/CacheService';
import { useNavigate } from 'react-router-dom';

const SeriesPage = ({ onCategorySelect, onMovieSelect, onSeriesSelect }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSeriesData = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🎬 SeriesPage: Fetching series data...');
      console.log('🔍 Base URL:', spotlightService.baseURL);
      console.log('🔍 Full URL:', spotlightService.baseURL + 'series/US/website?include_child_info=true');
      
      const data = await spotlightService.getSeriesHomePage();
      console.log('✅ SeriesPage: Data received:', data);

      // Process homepage data to extract categories with their channels
      const processedCategories =
        data.series?.map(item => ({
          _id: item.category._id,
          name: item.category.name,
          display_name: item.category.name,
          channels: item.channels || [],
          slug: item.category.slug,
        })) || [];

      setCategories(processedCategories);
    } catch (err) {
      console.error('Error fetching series data:', err);
      
      // Check if this is a 502 error (backend gateway issue)
      const is502Error = err.message.includes('502');
      const maxRetries = 2;
      
      if (is502Error && retryCount < maxRetries) {
        console.log(`SeriesPage: Retrying fetch (attempt ${retryCount + 1}/${maxRetries})...`);
        // Wait a bit before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchSeriesData(retryCount + 1);
      }
      
      // Provide a more specific error message for 502 errors
      if (is502Error) {
        setError(
          'The series service is temporarily unavailable (502 Bad Gateway). This is a backend server issue. Please try again in a few moments or contact support if the problem persists.'
        );
      } else {
        setError(`Failed to load series: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have preloaded data in cache first
    const checkPreloadedData = async () => {
      try {
        // Check if we have cached data from header preloading
        if (cacheService.hasSeriesPage()) {
          // If we have cached data, try to fetch it immediately
          const data = await spotlightService.getSeriesHomePage();

          // Process homepage data to extract categories with their channels
          const processedCategories =
            data.series?.map(item => ({
              _id: item.category._id,
              name: item.category.name,
              display_name: item.category.name,
              channels: item.channels || [],
              slug: item.category.slug,
            })) || [];

          setCategories(processedCategories);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn(
          'SeriesPage: Failed to use preloaded data, falling back to normal fetch:',
          err,
        );
      }

      // If no preloaded data or it failed, fetch normally
      fetchSeriesData();
    };

    checkPreloadedData();
  }, []);

  // Redirect to homepage if channel query parameter is present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const channelSlug = searchParams.get('channel');

    if (channelSlug) {
      // Redirect to homepage with the channel parameter
      navigate(`/?channel=${channelSlug}`, { replace: true });
    }
  }, [location.search, navigate]);

  if (error) {
    const is502Error = error.includes('502');
    
    return (
      <div className="min-h-screen series-page bg-primary">
        <Header />
        <main className="container px-6 py-8 mx-auto">
          <div className={`p-6 text-center border rounded-lg ${
            is502Error 
              ? 'border-yellow-500 bg-yellow-900/20' 
              : 'border-red-500 bg-red-900/30'
          }`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-12 h-12 mx-auto mb-4 ${
                is502Error ? 'text-yellow-500' : 'text-red-500'
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {is502Error ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              )}
            </svg>
            <h2 className="mb-2 text-xl font-bold text-white">
              {is502Error ? 'Service Temporarily Unavailable' : 'Error Loading Series'}
            </h2>
            <p className="max-w-2xl mx-auto mb-4 text-white/70">{error}</p>
            {is502Error && (
              <div className="max-w-2xl p-4 mx-auto mb-4 text-sm text-left rounded-lg bg-white/5">
                <p className="mb-2 font-semibold text-white">Technical Details:</p>
                <ul className="space-y-1 text-white/60">
                  <li>• <strong>Error Code:</strong> 502 Bad Gateway</li>
                  <li>• <strong>Endpoint:</strong> /api/series/US/website</li>
                  <li>• <strong>Cause:</strong> Backend API server issue</li>
                  <li>• <strong>Note:</strong> Movies page works normally, confirming this is specific to the series endpoint</li>
                </ul>
              </div>
            )}
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 font-medium text-white rounded-full bg-accent hover:bg-accent/80"
              >
                Go Home
              </button>
              <button
                onClick={() => navigate('/movies')}
                className="px-6 py-2 font-medium text-white rounded-full bg-blue-600 hover:bg-blue-700"
              >
                Browse Movies Instead
              </button>
              <button
                onClick={() => fetchSeriesData()}
                className="px-6 py-2 font-medium text-white rounded-full bg-white/10 hover:bg-white/20"
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen series-page bg-primary">
      <Header />

      <main className="container px-6 py-8 mx-auto">
        <h1 className="mb-8 text-3xl font-bold text-white">Series</h1>

        {/* Development debug info - Commented out for production */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div className="p-4 mb-6 bg-gray-800 rounded">
            <h3 className="mb-2 font-bold text-white">Debug Information:</h3>
            <div className="text-sm text-white/70">
              <p>Categories loaded: {categories.length}</p>
              {categories.length > 0 && (
                <p>First category: {categories[0].name}</p>
              )}
              <p>Using new authentication system: ✅</p>
            </div>
          </div>
        )} */}

        <ErrorBoundary fallback="Failed to load series categories">
          <Suspense fallback={<LoadingSpinner message="Loading series categories..." />}>
            {loading ? (
              <LoadingSpinner message="Loading series..." />
            ) : categories.length > 0 ? (
              categories.map(category => (
                <CategoryRail
                  key={category._id}
                  category={category}
                  onCategorySelect={onCategorySelect}
                  onMovieSelect={onMovieSelect}
                  onSeriesSelect={onSeriesSelect}
                />
              ))
            ) : (
              <div className="py-12 text-center text-white/50">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-16 h-16 mx-auto mb-4 text-white/30"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                  />
                </svg>
                <h3 className="mb-2 text-lg font-medium text-white">No Series Available</h3>
                <p className="text-white/50">Check back later for new series content.</p>
              </div>
            )}
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />
    </div>
  );
};

export default SeriesPage;
